import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Building2, MapPin, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, ITALIAN_REGIONS } from "../../api";
import ImageUploadField from "../../components/ImageUploadField";

const EMPTY = (kind) => ({
  name: "", city: "", country: "Italia", region: "", description: "", image_url: "",
  kind, lat: null, lng: null, pass_price_eur: null, active: true,
});

export default function MuseumsManager({ kind = "museum" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const labelPlural = kind === "city" ? "Città d'arte" : "Musei";
  const labelSingular = kind === "city" ? "Città" : "Museo";

  async function load() {
    setLoading(true);
    try {
      const r = await api.adminListMuseums();
      setItems((r.items || []).filter((m) => (m.kind || "museum") === kind));
    } catch (e) {
      toast.error("Errore caricamento", { description: e.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [kind]); // eslint-disable-line

  async function save() {
    const body = { ...editing };
    if (body.lat === "") body.lat = null;
    if (body.lng === "") body.lng = null;
    if (body.pass_price_eur === "" || body.pass_price_eur === null) body.pass_price_eur = null;
    else body.pass_price_eur = Number(body.pass_price_eur);
    if (typeof body.lat === "string") body.lat = body.lat ? Number(body.lat) : null;
    if (typeof body.lng === "string") body.lng = body.lng ? Number(body.lng) : null;
    body.kind = kind;
    try {
      if (editing.id) {
        await api.adminUpdateMuseum(editing.id, body);
        toast.success(`${labelSingular} aggiornato`);
      } else {
        await api.adminCreateMuseum(body);
        toast.success(`${labelSingular} creato`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Salvataggio fallito", { description: e.message });
    }
  }

  async function remove(id) {
    if (!window.confirm(`Eliminare definitivamente?`)) return;
    try {
      await api.adminDeleteMuseum(id);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error("Eliminazione fallita", { description: e.message });
    }
  }

  return (
    <div className="p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-4xl text-ink">{labelPlural}</h1>
          <p className="text-sm text-ink-soft mt-1">{items.length} {kind === "city" ? "borghi" : "musei"} totali</p>
        </div>
        <button
          data-testid="add-museum-btn"
          onClick={() => setEditing(EMPTY(kind))}
          className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2.5 rounded-full font-semibold transition"
        >
          <Plus size={18} /> Nuovo {labelSingular}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((m) => (
            <div key={m.id} data-testid={`museum-row-${m.id}`} className="bg-white border border-border rounded-2xl p-4 flex gap-4 hover:shadow-md transition">
              {m.image_url ? (
                <img
                  src={m.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${m.image_url}` : m.image_url}
                  alt={m.name}
                  onError={(e) => { e.target.style.display = "none"; }}
                  className="w-20 h-20 rounded-xl object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-muted flex items-center justify-center">
                  {kind === "city" ? <MapPin className="text-ink-soft" /> : <Building2 className="text-ink-soft" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-ink truncate">{m.name}</div>
                <div className="text-xs text-ink-soft truncate">{[m.city, m.region].filter(Boolean).join(" · ")}</div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[11px] font-medium text-brand">{m.artwork_count} opere</span>
                  {m.pass_price_eur && <span className="text-[11px] font-medium text-gold">Pass €{Number(m.pass_price_eur).toFixed(2)}</span>}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setEditing({ ...m })}
                  className="w-9 h-9 rounded-lg bg-bg hover:bg-brand/10 hover:text-brand flex items-center justify-center transition"
                ><Edit2 size={16} /></button>
                <button
                  onClick={() => remove(m.id)}
                  className="w-9 h-9 rounded-lg bg-bg hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition"
                ><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white">
              <h2 className="font-heading font-bold text-2xl text-ink">{editing.id ? "Modifica" : "Nuovo"} {labelSingular}</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-bg"><X /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <Field label="Nome" full>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="form-input" />
              </Field>
              <Field label="Città">
                <input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className="form-input" />
              </Field>
              <Field label="Paese">
                <input value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value })} className="form-input" />
              </Field>
              <Field label="Regione">
                <select value={editing.region || ""} onChange={(e) => setEditing({ ...editing, region: e.target.value })} className="form-input">
                  <option value="">—</option>
                  {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Pass price (€)">
                <input type="number" step="0.01" value={editing.pass_price_eur ?? ""} onChange={(e) => setEditing({ ...editing, pass_price_eur: e.target.value })} className="form-input" />
              </Field>
              <Field label="Descrizione" full>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="form-input resize-none" />
              </Field>
              <Field label="Image URL" full>
                <ImageUploadField
                  value={editing.image_url}
                  onChange={(v) => setEditing({ ...editing, image_url: v })}
                  testId="museum-image-upload"
                />
              </Field>
              <Field label="Latitudine">
                <input type="number" step="any" value={editing.lat ?? ""} onChange={(e) => setEditing({ ...editing, lat: e.target.value })} className="form-input" />
              </Field>
              <Field label="Longitudine">
                <input type="number" step="any" value={editing.lng ?? ""} onChange={(e) => setEditing({ ...editing, lng: e.target.value })} className="form-input" />
              </Field>
            </div>
            <div className="flex gap-2 justify-end px-6 py-4 border-t border-border sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl bg-bg text-ink-soft font-medium hover:bg-border transition">Annulla</button>
              <button data-testid="save-museum-btn" onClick={save} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2 rounded-xl font-semibold transition">
                <Save size={16} /> Salva
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.form-input{width:100%;padding:.65rem .9rem;border:1px solid hsl(var(--border));border-radius:.7rem;background:#fff;font-size:.875rem;color:#1c1c1e;outline:none;transition:.15s}.form-input:focus{border-color:#0B4A99}`}</style>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">{label.toUpperCase()}</label>
      {children}
    </div>
  );
}
